import { AIChatBox } from "@/components/ai-chatbox";
import { FireIcon } from "@/components/icons";
import { LoadingHoc } from "@/components/loading-hoc";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Rating } from "@/components/ui/rating";
import { useMobile } from "@/hooks/useMedia";
import { authStore } from "@/lib/auth/store";
import { tryResponse } from "@/lib/request";
import {
	cn,
	forTagBgColor,
	formatPopularity,
	formatTimestampAuto,
	fromNow,
} from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import NiceModal from "@ebay/nice-modal-react";
import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { useCountDown, useRequest } from "ahooks";
import { produce } from "immer";
import {
	CheckIcon,
	Ellipsis,
	MessageSquareMore,
	PencilLine,
	Plus,
	Trash2,
	XIcon,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
	addCreationScore,
	createDialogue,
	deleteDialogue,
	getCreationInfo,
	getLatestEditTime,
	likeCreationComment,
	listDialogue,
	renameDialogue,
	reportCreationView,
	undoLikeCreationComment,
} from "./-api";
import { CommentPanel } from "./-comps/comment-panel";
import { DeleteDialogueModal } from "./-comps/delete-modal";
import { CommentLikedIcon, CommentUnlikedIcon } from "./-comps/icons";
import { Logic } from "./-logic";

type ProductSearch = {
	shared?: boolean;
	openComment?: boolean;
	isChatting?: boolean;
};

export const Route = createFileRoute("/$lang/story/$creation_id/$dialogue_id")({
	component: RouteComponent,
	validateSearch: (search): ProductSearch => {
		return {
			shared: search.shared as boolean | undefined,
			openComment: search.openComment as boolean | undefined,
			isChatting: search.isChatting as boolean | undefined,
		};
	},
	async beforeLoad({ params: { creation_id, dialogue_id }, search }) {
		if (dialogue_id === "-") {
			const authToken = localStorage.getItem("AUTH_TOKEN");
			if (authToken) {
				const dt = await listDialogue(creation_id);
				const lastDialogueId = dt?.data?.list?.[0]?.dialogue_id;
				if (lastDialogueId) {
					throw redirect({
						to: localizeHref(`/story/${creation_id}/${lastDialogueId}`),
						search,
					});
				}
				const newDialogue = await createDialogue(creation_id);
				throw redirect({
					to: localizeHref(
						`/story/${creation_id}/${newDialogue?.data?.dialogue_id}`,
					),
					search,
				});
			}
		}
	},
	loader: async ({ params: { creation_id } }) => {
		const creationInfo = await tryResponse(getCreationInfo(creation_id));
		return {
			creationInfo,
		};
	},
});

function RouteComponent() {
	return (
		<Logic.Provider>
			<RouteComponentInner />
		</Logic.Provider>
	);
}

function RouteComponentInner() {
	const { creationInfo } = Route.useLoaderData();
	const { openComment, isChatting } = Route.useSearch();
	const { setOpenComment } = Logic.useContainer();

	useCheckLatestVersion();

	// 如果 URL 参数中有 openComment=true，则打开评论面板
	React.useEffect(() => {
		if (openComment) {
			setOpenComment(true);
		}
	}, [openComment, setOpenComment]);

	const isMobile = useMobile();

	if (isMobile) {
		return (
			<div className={cn("mx-auto text-sm flex gap-2 px-4 -mt-4 relative")}>
				{isChatting ? (
					<div className="fixed top-0 left-0 w-full h-full">
						<Content info={creationInfo} />
					</div>
				) : (
					<Sidebar info={creationInfo} />
				)}
				<div className="fixed bottom-0 h-1/2">
					<CommentPanel />
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"mx-auto md:max-w-7xl text-sm flex gap-2 px-4 md:px-0 -mt-4 md:mt-0",
			)}
		>
			<Sidebar info={creationInfo} />
			<Content info={creationInfo} />
		</div>
	);
}

function Sidebar({ info }: { info: any }) {
	const { creation_id } = Route.useParams();
	const { shared } = Route.useSearch();
	const user = useStore(authStore, (s) => s.user);
	return (
		<div
			className={cn(
				"md:w-[300px] w-full bg-card rounded-2xl py-6 md:py-5 px-8 md:px-4  md:h-[calc(100vh-134px)] overflow-y-auto",
			)}
			style={{
				boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.05)",
			}}
		>
			<h6 className="text-t1 text-lg leading-6 mb-3.5 line-clamp-2 break-words">
				{info?.title}
			</h6>
			<div className="mb-3.5 flex flex-row gap-2 items-center">
				<img
					src={info?.author?.avatar}
					alt=""
					className="size-4 rounded-full object-cover bg-bg2"
				/>
				<div className="text-t2 text-xs  max-w-1/2 truncate">
					{info?.author?.name}
				</div>
				<div className="text-t2 text-xs ml-auto">
					{info?.created_time ? fromNow(info?.created_time / 1000000000) : "-"}
				</div>
			</div>
			<TagList tags={info?.tags?.map((tag: any) => tag.name) || []} />
			<p className="text-t2 text-sm leading-6 mt-2.5 line-clamp-6 break-words">
				{info?.brief_introduction}
			</p>
			<div className="flex flex-row gap-2 items-center mt-2.5 justify-between">
				<div className="flex flex-row gap-1.5 items-center">
					<FireIcon className="w-3 h-3" />
					<span className="text-t3 text-xs">
						{formatPopularity(info?.popularity ?? 0)}
					</span>
				</div>
				<div className="flex flex-row gap-1.5 items-center">
					<span className="text-t3 text-xs">{m.story_my_score()}</span>
					<Rating
						key={info?.player_score}
						initialRating={info?.player_score ?? 0}
						onRatingChange={(rating) => {
							addCreationScore(creation_id, rating);
						}}
						readonly={shared || !user}
					/>
				</div>
			</div>
			<div className="flex flex-col-reverse md:flex-col">
				{shared ? <ShareChatHistory /> : <ChatHistory />}
				<CommentHistory />
			</div>
		</div>
	);
}

function ShareChatHistory() {
	const { shareDialogueInfo } = Logic.useContainer();
	const nav = Route.useNavigate();
	if (!shareDialogueInfo) {
		return null;
	}

	return (
		<div className="mt-6">
			<div className="text-t1 text-xs">
				{m.story_history_dialogue({
					count: 1,
				})}
			</div>
			<div className="flex flex-col gap-2 mt-3 max-h-[138px] pointer-events-none">
				<HistoryItem
					it={shareDialogueInfo?.dialogue || {}}
					refreshDialogueList={() => {}}
					list={[]}
					mutateDialogueList={() => {}}
				/>
			</div>
			<div
				onClick={() => {
					nav({
						search: {
							isChatting: true,
							shared: true,
						},
					});
				}}
				className={cn(
					"bg-main1 text-w1 rounded-full py-2.5 flex items-center justify-center mt-3 cursor-pointer transition-all md:hidden",
				)}
			>
				{m.story_view_chat()}
			</div>
		</div>
	);
}

function ChatHistory() {
	const { creation_id } = Route.useParams();
	const { shared } = Route.useSearch();
	const user = useStore(authStore, (s) => s.user);
	const doLogin = useStore(authStore, (s) => s.doLogin);

	const {
		data: dialogueList,
		refreshAsync: refreshDialogueList,
		mutate: mutateDialogueList,
	} = useRequest(
		() => {
			return tryResponse(listDialogue(creation_id));
		},
		{
			ready: !shared && !!user,
			refreshDeps: [creation_id],
		},
	);
	const nav = Route.useNavigate();
	const isMobile = useMobile();

	function createNewDialogue() {
		if (!user) {
			doLogin();
			return;
		}
		return tryResponse(createDialogue(creation_id)).then(async (dt) => {
			mutateDialogueList((jt: any) => ({
				total: jt?.total + 1,
				list: [dt, ...(jt?.list || [])],
			}));
			nav({
				params: {
					dialogue_id: dt?.dialogue_id,
				},
			});
		});
	}

	if (!user && !isMobile) {
		return null;
	}

	return (
		<div className="mt-6">
			<div className="text-t1 text-xs flex items-center justify-between">
				<div>
					{m.story_history_dialogue({
						count: dialogueList?.total || 0,
					})}
				</div>
				<LoadingHoc>
					{({ isLoading, loadingIcon, wrapPromise }) => (
						<div
							className={cn(
								"items-center gap-1 cursor-pointer flex md:hidden",
								shared && "hidden",
							)}
							onClick={() => {
								wrapPromise(createNewDialogue());
							}}
						>
							{isLoading ? (
								loadingIcon("size-5")
							) : (
								<div className="rounded-full bg-t3 p-0.5">
									<Plus className="size-4 text-chat1" />
								</div>
							)}

							{m.story_add_new_dialogue()}
						</div>
					)}
				</LoadingHoc>
			</div>
			<div className="flex flex-col gap-2 mt-3 max-h-[138px] overflow-y-auto">
				{dialogueList?.list?.map((it: any) => {
					return (
						<HistoryItem
							key={it.dialogue_id}
							it={it}
							refreshDialogueList={refreshDialogueList}
							list={dialogueList?.list}
							mutateDialogueList={mutateDialogueList}
						/>
					);
				})}
			</div>
			{isMobile ? (
				<div
					onClick={() => {
						// 未登陆暂时不展示历史对话，暂不处理
						if (!user) {
							doLogin();
							return;
						}
						nav({
							search: {
								isChatting: true,
							},
						});
					}}
					className={cn(
						"bg-main1 text-w1 rounded-full py-2.5 flex items-center justify-center mt-3 cursor-pointer  transition-all",
					)}
				>
					{shared ? m.story_view_chat() : m.story_start_chat()}
				</div>
			) : (
				<LoadingHoc>
					{({ isLoading, loadingIcon, wrapPromise }) => (
						<div
							onClick={() => {
								wrapPromise(createNewDialogue());
							}}
							className={cn(
								"bg-bg2 rounded-full py-2.5 flex items-center justify-center mt-3 cursor-pointer hover:bg-bg4 dark:hover:bg-main1 dark:hover:text-w1 transition-all",
								shared && "hidden",
							)}
						>
							{isLoading ? (
								loadingIcon("size-3.5 mr-1")
							) : (
								<Plus className="size-3.5 mr-1" />
							)}
							{m.story_create_new_dialogue()}
						</div>
					)}
				</LoadingHoc>
			)}
		</div>
	);
}

function useCheckLatestVersion() {
	const { creation_id } = Route.useParams();
	const { creationInfo } = Route.useLoaderData();
	const { shared } = Route.useSearch();
	const user = useStore(authStore, (s) => s.user);
	const nav = Route.useNavigate();

	useRequest(
		() => {
			return reportCreationView(creation_id);
		},
		{
			ready: !shared && !!user,
			refreshDeps: [creation_id],
		},
	);

	const [countdown] = useCountDown({
		leftTime: 1000 * 5,
	});
	useRequest(
		() => {
			return getLatestEditTime(creation_id).then((rt) => {
				if (rt.data?.status === "UNPUBLISHED") {
					setTimeout(() => {
						nav({
							to: localizeHref("/"),
						});
					}, 1000);
					return;
				}
				if (
					rt.data?.latest_edit_time &&
					creationInfo?.latest_edit_time &&
					rt.data?.latest_edit_time > creationInfo?.latest_edit_time
				) {
					toast.info(m.story_creation_has_been_updated());
					setTimeout(() => {
						window.location.reload();
					}, 1000);
				}
			});
		},
		{
			ready: creation_id !== "-" && countdown === 0,
			refreshDeps: [],
			pollingInterval: 1000 * 20, //todo：生成环境改回10s
		},
	);
}

function HistoryItem({
	it,
	list,
	mutateDialogueList,
}: {
	it: any;
	refreshDialogueList: () => void;
	list: any;
	mutateDialogueList: any;
}) {
	const nav = Route.useNavigate();
	const { dialogue_id, creation_id } = Route.useParams();
	const [isRename, setIsRename] = useState(false);
	const [renameValue, setRenameValue] = useState(it.name);
	const inputRef = useRef<HTMLInputElement>(null);
	return (
		<div
			className={cn(
				"py-2.5 px-5.5 bg-bg1 rounded-full text-t2 flex flex-row gap-2 items-center cursor-pointer",
				dialogue_id === it.dialogue_id && "bg-c1",
			)}
			key={it.dialogue_id}
			onClick={() => {
				nav({
					params: {
						dialogue_id: it.dialogue_id,
					},
				});
			}}
		>
			<MessageSquareMore className="size-4.5 flex-shrink-0" />
			{isRename ? (
				<input
					ref={inputRef}
					type="text"
					className="text-t1 outline-none flex-1 w-0"
					value={renameValue}
					onChange={(e) => setRenameValue(e.target.value)}
				/>
			) : (
				<span className="truncate"> {it.name}</span>
			)}
			{isRename ? (
				<>
					<LoadingHoc>
						{({ isLoading, loadingIcon, wrapPromise }) =>
							isLoading ? (
								loadingIcon("size-4.5 cursor-pointer text-t1 flex-shrink-0")
							) : (
								<CheckIcon
									className="size-4.5 ml-auto cursor-pointer text-t1 flex-shrink-0"
									onClick={() => {
										wrapPromise(
											tryResponse(
												renameDialogue(it.dialogue_id, renameValue),
											).then(() => {
												mutateDialogueList((jt: any) => ({
													total: jt?.total,
													list: jt?.list?.map((item: any) =>
														item.dialogue_id === it.dialogue_id
															? { ...item, name: renameValue }
															: item,
													),
												}));
												setRenameValue("");
												setIsRename(false);
											}),
										);
									}}
								/>
							)
						}
					</LoadingHoc>

					<XIcon
						className="size-4.5  cursor-pointer text-t1 flex-shrink-0"
						onClick={() => {
							setIsRename(false);
							setRenameValue("");
						}}
					/>
				</>
			) : (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Ellipsis
							className="size-4.5 ml-auto text-t3 flex-shrink-0"
							onClick={(e) => {
								e.stopPropagation();
							}}
						/>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="text-t2 text-sm border-none">
						<DropdownMenuItem
							onClick={() => {
								setIsRename(true);
								setRenameValue(it.name);
								setTimeout(() => {
									inputRef.current?.focus();
								}, 200);
							}}
						>
							<PencilLine className="text-t2" />
							{m.common_rename()}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<LoadingHoc>
							{({ isLoading, loadingIcon, wrapPromise }) => (
								<DropdownMenuItem
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										NiceModal.show(DeleteDialogueModal, {
											onConfirm: async () => {
												await wrapPromise(
													tryResponse(deleteDialogue(it.dialogue_id)).then(
														async () => {
															mutateDialogueList((jt: any) => ({
																total: jt?.total - 1,
																list: jt?.list?.filter(
																	(item: any) =>
																		item.dialogue_id !== it.dialogue_id,
																),
															}));

															if (it.dialogue_id !== dialogue_id) {
																return;
															}
															const otherList = (list || []).filter(
																(item: any) =>
																	item.dialogue_id !== it.dialogue_id,
															);
															if (otherList.length > 0) {
																nav({
																	params: {
																		dialogue_id: otherList?.[0]?.dialogue_id,
																	},
																});
															} else {
																await tryResponse(
																	createDialogue(creation_id),
																).then((dt) => {
																	mutateDialogueList({
																		total: 1,
																		list: [dt],
																	});
																	nav({
																		params: {
																			dialogue_id: dt?.dialogue_id,
																		},
																	});
																});
															}
														},
													),
												);
											},
										});
									}}
								>
									{isLoading ? (
										loadingIcon("text-t2")
									) : (
										<Trash2 className="text-t2" />
									)}
									{m.common_delete()}
								</DropdownMenuItem>
							)}
						</LoadingHoc>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
}

function CommentHistory() {
	const { setOpenComment, commentListData, mutateCommentListData } =
		Logic.useContainer();
	const { shared } = Route.useSearch();
	const user = useStore(authStore, (s) => s.user);
	return (
		<div className={cn("mt-6", (shared || !user) && "pointer-events-none")}>
			<div className="text-t1 text-xs">
				{m.story_comment_count({ count: commentListData?.data?.total || 0 })}
			</div>
			<div className="flex flex-col gap-2 mt-3">
				{commentListData?.data?.list?.map((it: any) => {
					return (
						<div
							key={it.comment_id}
							className="py-2.5 px-3 bg-bg1 rounded-3xl"
							onClick={() => {
								setOpenComment(true);
							}}
						>
							<div className="flex items-center ">
								<img
									alt=""
									src={it?.user?.avatar}
									className="size-4 rounded-full bg-bg2"
								/>
								<span className="text-t3 text-xs ml-2 truncate max-w-2/5">
									{it?.user?.name}
								</span>
								<span className="text-t3 text-xs ml-auto">
									{formatTimestampAuto(it?.created_time, "YYYY/MM/DD HH:mm")}
								</span>
							</div>
							<div className="text-t1 mt-3.5 line-clamp-3 break-words">
								{it?.content}
							</div>
							<div className="flex mt-2">
								<span
									className={cn(
										"ml-auto text-t2 text-xs flex items-center gap-1 cursor-pointer select-none",
										it.liked && "text-main1",
									)}
									onClick={(e) => {
										e.stopPropagation();
										if (it?.liked) {
											mutateCommentListData(
												produce((draft) => {
													const obj = draft?.data?.list?.find(
														(jt: any) => jt.comment_id === it.comment_id,
													);
													obj.liked = false;
													obj.likes = obj.likes - 1;
												}),
											);
											undoLikeCreationComment(it.comment_id);
										} else {
											mutateCommentListData(
												produce((draft) => {
													const obj = draft?.data?.list?.find(
														(item: any) => item.comment_id === it.comment_id,
													);
													obj.liked = true;
													obj.likes = (obj.likes || 0) + 1;
												}),
											);
											likeCreationComment(it.comment_id);
										}
									}}
								>
									{it?.liked ? <CommentLikedIcon /> : <CommentUnlikedIcon />}
									<span className="">{it.likes || 0}</span>
								</span>
							</div>
						</div>
					);
				})}
			</div>
			<div
				className={cn(
					"bg-bg2 rounded-full py-2.5 flex items-center justify-center mt-3 cursor-pointer hover:bg-bg4 transition-all dark:hover:bg-main1 dark:hover:text-w1",
					(shared || !user) && "hidden",
				)}
				onClick={() => {
					setOpenComment(true);
				}}
			>
				<Plus className="size-3.5 mr-1" />
				{m.story_comment_publish()}
			</div>
		</div>
	);
}

function TagList({ tags }: { tags: string[] }) {
	return (
		<div className="flex flex-row gap-2 items-center flex-wrap max-h-14 overflow-hidden">
			{tags.map((tag, _index) => (
				<span
					key={`TagList-${tag}-${_index}`}
					className={cn(
						"text-t2 text-xs rounded-full px-3 h-[24px] flex items-center justify-center",
						forTagBgColor(tag),
					)}
				>
					{tag}
				</span>
			))}
		</div>
	);
}

function Content({ info }: { info: any }) {
	const { creation_id, dialogue_id } = Route.useParams();
	const { shared } = Route.useSearch();
	const { shareDialogueInfo } = Logic.useContainer();
	const isMobile = useMobile();

	return (
		<AIChatBox
			key={`${creation_id}-${dialogue_id}`}
			className="md:h-[calc(100vh-134px)] h-full"
			creation_id={creation_id}
			dialogue_id={dialogue_id}
			isPreview={false}
			isShare={!!shared}
			prologue={info?.prologue || ""}
			cover_image={info?.cover_image || ""}
			userAvatar={shareDialogueInfo?.user?.avatar || ""}
			background_image={info?.background_image || ""}
			detailed_introduction={info?.detailed_introduction || ""}
			comment_panel={
				isMobile
					? undefined
					: (boxWrapperRef) => <CommentPanel containerRef={boxWrapperRef} />
			}
		/>
	);
}
